/**
 * Client-side unit conversion utilities for the Telemetry card.
 *
 * The pipeline always outputs values in their canonical unit (e.g. W, Wh, V, A).
 * This module lets the UI convert those values to user-preferred units without
 * touching the server.
 *
 * Conversion formula:
 *   displayValue = canonicalValue * (canonicalFactor / preferredFactor)
 *
 * Example: 2500 W → kW: 2500 * (1 / 1000) = 2.5
 */

// Each family is an array of [symbol, factor-to-base] pairs, ordered
// from smallest to largest for consistent dropdown display.
interface UnitDef {
  symbol: string;
  label: string;
  /** Factor to multiply by to reach the family's base unit. */
  factor: number;
}

type UnitFamily = UnitDef[];

const UNIT_FAMILIES: UnitFamily[] = [
  // Power
  [
    { symbol: 'mW',  label: 'Milliwatts (mW)',   factor: 0.001 },
    { symbol: 'W',   label: 'Watts (W)',          factor: 1 },
    { symbol: 'kW',  label: 'Kilowatts (kW)',     factor: 1_000 },
    { symbol: 'MW',  label: 'Megawatts (MW)',      factor: 1_000_000 },
  ],
  // Energy
  [
    { symbol: 'mWh', label: 'Milliwatt-hours (mWh)', factor: 0.001 },
    { symbol: 'Wh',  label: 'Watt-hours (Wh)',        factor: 1 },
    { symbol: 'kWh', label: 'Kilowatt-hours (kWh)',   factor: 1_000 },
    { symbol: 'MWh', label: 'Megawatt-hours (MWh)',   factor: 1_000_000 },
  ],
  // Voltage
  [
    { symbol: 'mV',  label: 'Millivolts (mV)',    factor: 0.001 },
    { symbol: 'V',   label: 'Volts (V)',           factor: 1 },
    { symbol: 'kV',  label: 'Kilovolts (kV)',      factor: 1_000 },
  ],
  // Current
  [
    { symbol: 'mA',  label: 'Milliamps (mA)',      factor: 0.001 },
    { symbol: 'A',   label: 'Amps (A)',            factor: 1 },
  ],
  // Frequency
  [
    { symbol: 'Hz',  label: 'Hertz (Hz)',          factor: 1 },
    { symbol: 'kHz', label: 'Kilohertz (kHz)',     factor: 1_000 },
  ],
  // Apparent power
  [
    { symbol: 'VA',  label: 'Volt-Amps (VA)',      factor: 1 },
    { symbol: 'kVA', label: 'Kilovolt-Amps (kVA)', factor: 1_000 },
    { symbol: 'MVA', label: 'Megavolt-Amps (MVA)', factor: 1_000_000 },
  ],
  // Reactive power
  [
    { symbol: 'VAR',  label: 'Volt-Amps Reactive (VAR)',  factor: 1 },
    { symbol: 'kVAR', label: 'Kilovolt-Amps Reactive (kVAR)', factor: 1_000 },
  ],
];

function findFamily(unit: string): UnitFamily | null {
  return UNIT_FAMILIES.find((f) => f.some((u) => u.symbol === unit)) ?? null;
}

function findDef(unit: string): UnitDef | null {
  for (const family of UNIT_FAMILIES) {
    const def = family.find((u) => u.symbol === unit);
    if (def) return def;
  }
  return null;
}

/** Returns all unit options for a given unit symbol, or [] if no alternatives. */
export function getUnitOptions(unit: string): UnitDef[] {
  return findFamily(unit) ?? [];
}

/** Returns true if the unit belongs to a family with alternatives. */
export function hasUnitAlternatives(unit: string): boolean {
  const family = findFamily(unit);
  return family !== null && family.length > 1;
}

/**
 * Convert a numeric value from one unit to another within the same family.
 * Returns the original value unchanged if units are the same or unknown.
 */
export function convertUnit(value: number, fromUnit: string, toUnit: string): number {
  if (fromUnit === toUnit) return value;
  const fromDef = findDef(fromUnit);
  const toDef = findDef(toUnit);
  if (!fromDef || !toDef) return value;
  // value → base unit → target unit
  return (value * fromDef.factor) / toDef.factor;
}

/**
 * Format a converted value with an appropriate number of decimal places.
 * Uses catalog precision if provided; otherwise picks automatically.
 */
export function formatValue(value: number, precision?: number | null): string {
  if (precision != null) return value.toFixed(precision);
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 100_000) return value.toFixed(0);
  if (abs >= 1_000)   return value.toFixed(1);
  if (abs >= 10)      return value.toFixed(2);
  if (abs >= 0.1)     return value.toFixed(3);
  return value.toFixed(4);
}
